// Entry point. Boots the world, the UI, audio, input, and the fixed-timestep
// loop, then wires gameplay events to presentation. This is the only file that
// knows about every layer at once; everything below it stays decoupled.

import { GameLoop } from '@core/loop';
import { bus } from '@core/events';
import { loadGame, saveGame, serializeSave, parseSaveBlob } from '@core/save';
import { createInitialState, type GameState } from '@core/state';
import { VERSION } from '@data/version';
import { newsSince } from '@data/news';
import { initLang, t } from '@i18n/i18n';
import { track } from '@online/analytics';
import { api, getToken, clearToken } from '@online/api';
import { updateSim } from '@systems/sim';
import { callWaveNow } from '@systems/waves';
import { cursor, resetHarvest } from '@systems/harvest';
import { pickCard } from '@systems/ages';
import { resetBuildCooldown } from '@systems/buildings';
import { buySkill, restartRun, planetaryPrestige, retireRun } from '@systems/prestige';
import { buyTech } from '@systems/tech';
import { activateArtifact } from '@systems/artifacts';
import { applyOffline } from '@systems/offline';
import { WorldView } from '@render/world';
import { UI, type UIController } from '@ui/ui';
import { initAudio, setMuted } from '@audio/sfx';

const FIXED_DT = 1 / 30; // simulation step (seconds)
const AUTOSAVE_INTERVAL = 15000; // ms
const EMAIL_KEY = 'gokturk.email';

/** Copies a cloud save into the live state object in place (so closures keep
 *  their reference), but only when the cloud save is at least as far along. */
function adoptCloud(state: GameState, blob: string | null): void {
  if (!blob) return; // no cloud save yet; the local state will be pushed up
  const cloud = parseSaveBlob(blob);
  if (!cloud) return;
  if (cloud.gameTime >= state.gameTime) {
    state.run = cloud.run;
    state.meta = cloud.meta;
    state.pending = cloud.pending;
    state.settings = cloud.settings;
    state.gameTime = cloud.gameTime;
    state.lastSavedWall = cloud.lastSavedWall;
    state.version = cloud.version;
    state.metaPerks = cloud.metaPerks;
  }
}

/** Best-effort push of the current state to the cloud. Silent on failure (the
 *  local save still holds the progress). */
async function pushCloud(state: GameState): Promise<void> {
  if (!getToken()) return;
  try {
    await api.save(serializeSave(state), {
      version: VERSION,
      gameTime: state.gameTime,
      planet: state.meta.planet,
      bestWave: state.meta.bestWave,
    });
  } catch {
    /* offline or backend down; local save is the fallback */
  }
}

/** Logs the player in (resuming a stored session, or via the email-code screen)
 *  and adopts their cloud save. Required for the playtest. */
async function authenticate(state: GameState, ui: UI, world: WorldView): Promise<void> {
  if (getToken()) {
    try {
      const { save } = await api.load();
      adoptCloud(state, save);
      world.rebuildFromState(state);
      let email = '';
      try {
        email = localStorage.getItem(EMAIL_KEY) ?? '';
      } catch {
        /* ignore */
      }
      ui.setAccount(email || t('login.signedIn'));
      return;
    } catch (e) {
      if ((e as { status?: number }).status === 401) {
        clearToken(); // stale session: fall through to the login screen
      } else {
        ui.setAccount(t('login.offline')); // backend unreachable: play with the local save
        return;
      }
    }
  }
  const res = await ui.showLogin();
  adoptCloud(state, res.save);
  world.rebuildFromState(state);
  // Guest (empty token): nothing to adopt and nothing to remember. The run lives
  // in localStorage; the player can link it later from the account panel, and
  // because adoptCloud only overwrites when the CLOUD save is newer, linking a
  // fresh account keeps the guest progress and pushes it up on the next save.
  if (!res.token) {
    ui.setAccount('', true);
    return;
  }
  try {
    localStorage.setItem(EMAIL_KEY, res.email);
  } catch {
    /* ignore */
  }
  ui.setAccount(res.email);
}

async function boot(): Promise<void> {
  // 0. Language (English default; persisted choice or browser language)
  initLang();
  track('app_open');

  // 1. State
  const state: GameState = loadGame() ?? createInitialState();
  setMuted(state.settings.muted);

  // 2. World (async Pixi init)
  const host = document.getElementById('world-host');
  if (!host) throw new Error('world-host element missing');
  const world = new WorldView();
  await world.init(host);
  world.rebuildFromState(state);

  // 3. UI
  const uiRoot = document.getElementById('ui-root');
  if (!uiRoot) throw new Error('ui-root element missing');

  const controller: UIController = {
    onPickCard(id) {
      pickCard(state, id);
      ui.hideCards();
      saveGame(state);
    },
    onBuySkill(id) {
      buySkill(state, id);
      saveGame(state);
    },
    onBuyTech(id) {
      buyTech(state, id);
      saveGame(state);
    },
    onActivateArtifact(id) {
      activateArtifact(state, id);
      saveGame(state);
    },
    onRestart() {
      restartRun(state);
      ui.hideDeath();
      world.rebuildFromState(state);
      saveGame(state);
    },
    onCallWave() {
      if (callWaveNow(state)) track('wave_skip', { wave: state.run.wave + 1 });
    },
    onRetire() {
      if (retireRun(state)) saveGame(state);
    },
    onPrestige() {
      if (planetaryPrestige(state)) {
        ui.hideDeath(); // ascending can also be triggered from the death screen
        world.rebuildFromState(state);
        saveGame(state);
        void pushCloud(state);
      }
    },
    onHardReset() {
      // Wipe everything (meta included) by adopting a brand-new state in place,
      // then overwrite both the local and the cloud save. Mute is kept.
      const fresh = createInitialState();
      state.run = fresh.run;
      state.meta = fresh.meta;
      state.pending = fresh.pending;
      state.gameTime = fresh.gameTime;
      state.lastSavedWall = fresh.lastSavedWall;
      state.version = fresh.version;
      state.metaPerks = fresh.metaPerks;
      resetHarvest();
      resetBuildCooldown();
      ui.hideCards();
      ui.hideDeath();
      world.rebuildFromState(state);
      saveGame(state);
      void pushCloud(state);
    },
    onToggleMute() {
      state.settings.muted = !state.settings.muted;
      setMuted(state.settings.muted);
      saveGame(state);
      return state.settings.muted;
    },
    onSignIn() {
      // A guest linking an account mid-run. adoptCloud keeps whichever save is
      // further along, so a fresh account cannot wipe the run they just played;
      // pushCloud then puts it on the server.
      void (async () => {
        const res = await ui.showLogin();
        if (!res.token) return; // they backed out again: stay a guest
        adoptCloud(state, res.save);
        world.rebuildFromState(state);
        try {
          localStorage.setItem(EMAIL_KEY, res.email);
        } catch {
          /* ignore */
        }
        ui.setAccount(res.email);
        saveGame(state);
        void pushCloud(state);
      })();
    },
  };

  const ui = new UI(uiRoot, controller);
  ui.build(state.settings.muted);

  // 4. Audio (event-driven cues)
  initAudio(state.settings.muted);

  // Paint one frame so the login screen sits over the world, not a black void.
  world.update(state, 0);
  ui.update(state);

  // 5. Auth + cloud save (blocks until logged in; state is adopted in place).
  await authenticate(state, ui, world);
  track('login');

  // First-timers get the opening story; returning players get a "what's new"
  // panel for anything that changed since their last visit.
  let lastSeenVersion: string | null = null;
  try {
    lastSeenVersion = localStorage.getItem('gokturk.lastSeenVersion');
    localStorage.setItem('gokturk.lastSeenVersion', VERSION);
  } catch {
    /* ignore */
  }
  if (!lastSeenVersion) {
    ui.showIntroIfNew(); // intro -> "Başla" -> tutorial
  } else {
    const news = newsSince(lastSeenVersion);
    if (news.length) ui.showWhatsNew(news); // news -> "Devam" -> tutorial
    else ui.startTutorial(); // nothing to show; run the tutorial if not seen yet
  }

  // 6. Offline progress (after presentation is ready so we can report it)
  const offline = applyOffline(state);
  if (offline) ui.showOffline(offline.seconds, offline.gained);

  // 6. Input: the cursor harvest circle follows the pointer over the play area
  const setCursor = (clientX: number, clientY: number): void => {
    const local = world.toLocal(clientX, clientY);
    cursor.x = local.x;
    cursor.y = local.y;
  };
  host.addEventListener('pointermove', (e) => {
    // Touch fires pointermove only while a finger is down (drag-sweep); mouse fires
    // on hover. Either way the harvest circle follows and stays active.
    setCursor(e.clientX, e.clientY);
    cursor.active = true;
  });
  host.addEventListener('pointerdown', (e) => {
    // Tap-to-place: the circle settles where you tap and keeps harvesting there.
    setCursor(e.clientX, e.clientY);
    cursor.active = true;
  });
  host.addEventListener('pointerenter', (e) => {
    if (e.pointerType !== 'touch') cursor.active = true; // mouse hover
  });
  host.addEventListener('pointerleave', (e) => {
    // Mouse leaving the board lifts the cursor. Touch: the placed circle stays put
    // (tap-to-place) so you never have to keep a finger pressed.
    if (e.pointerType !== 'touch') cursor.active = false;
  });

  // Park the circle on the settlement before the first input. The tutorial's very
  // first line points at "the yellow circle", which on a phone did not exist yet:
  // nothing is drawn until a pointer event, so a new player was told to move a
  // thing they could not see. Starting it on the keep also means the harvest verb
  // is demonstrated the moment they drag it onto a node.
  cursor.x = host.clientWidth / 2;
  cursor.y = host.clientHeight / 2;
  cursor.active = true;

  // 7. Overlay transitions driven by gameplay events
  bus.on('age:advance', () => ui.showCards(state));
  bus.on('death', () => {
    saveGame(state);
    void pushCloud(state);
    ui.showDeath(state);
  });

  // Analytics funnel: the moments that tell us where players engage and drop off.
  let firstHarvestSent = false;
  bus.on('harvest:hit', () => {
    if (!firstHarvestSent) {
      firstHarvestSent = true;
      track('first_harvest');
    }
  });
  bus.on('wave:result', ({ win, wave }) => track('wave_result', { wave, win }));
  bus.on('age:advance', ({ age }) => track('age_advance', { age })); // payload = the NEW age
  bus.on('death', ({ wave }) => track('death', { wave, age: state.run.age, retired: state.pending.retired })); // wave = the fatal wave, matching wave_result
  bus.on('calamity', ({ id }) => track('calamity', { id, wave: state.run.wave, age: state.run.age }));

  // Restore any overlay a loaded save was sitting on
  if (state.pending.cardChoices) ui.showCards(state);
  if (state.pending.dead) ui.showDeath(state);

  // 8. The loop. The Pixi world renders every frame; the DOM HUD only needs
  // ~10Hz (idle-game numbers; the progress bars have a 0.2s CSS transition that
  // smooths the cadence), which keeps ~40 DOM writes/frame off phone main threads.
  let uiAcc = 1; // >0 so the first frame paints immediately
  const loop = new GameLoop(
    FIXED_DT,
    (dt) => updateSim(state, dt),
    (frameDt) => {
      world.update(state, frameDt);
      uiAcc += frameDt;
      if (uiAcc >= 0.1) {
        uiAcc = 0;
        ui.update(state);
      }
      ui.tickFeedback(frameDt);
    },
  );
  loop.start();

  // 9. Persistence
  // Do not autosave while hidden: that would keep advancing the offline
  // baseline during time the loop is not simulating, swallowing the progress.
  window.setInterval(() => {
    if (!document.hidden) {
      saveGame(state);
      void pushCloud(state);
    }
  }, AUTOSAVE_INTERVAL);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      saveGame(state);
      void pushCloud(state);
    } else {
      // Returning to a hidden-but-open tab: requestAnimationFrame was paused, so
      // credit the gap as offline progress (applyOffline re-stamps lastSavedWall,
      // so the resumed loop does not double-count).
      const report = applyOffline(state);
      if (report) ui.showOffline(report.seconds, report.gained);
    }
  });
  window.addEventListener('beforeunload', () => saveGame(state));

  // Expose a tiny debug handle without leaking into normal play.
  (window as unknown as { __eons?: unknown }).__eons = { state, world, ui };
}

boot().catch((err) => {
  console.error('[eons] failed to start:', err);
  const root = document.getElementById('ui-root');
  if (root) {
    root.innerHTML =
      '<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#ff5a5a;font-family:system-ui;text-align:center;padding:24px">' +
      'GÖKTÜRK başlatılamadı. Konsolu kontrol et.</div>';
  }
});
